import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { Modal } from '../../../../customComponents/Modal'
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { AddRCNPD, GetIsdsAndDistrictsForPlanOrganization, UpdateRCNPD } from '../../../../../services/AnnualPlanService'
import { type OrganizationOption } from '../../../../../models/requestObjects/CommonObjects'
import { ActivityEditSession } from '../ActivityEditSession'
import { PriorityOneActivityName } from '../../../../../models/PriorityOneActivityName'
import { ContentFocus } from '../../../../../models/ContentFocus'
import { TrainingLength } from '../../../../../models/TrainingLength'
import { DatePickerWithLabel } from '../../../../customComponents/DatePickerWithLabel'
import { type Response } from '../../../../../hooks/use-fetch'
import { AddCircle, ExpandMore, RemoveCircle } from '@mui/icons-material'
import { type AddRCNPDContractSessionFormValues, type AddRCNPDSeriesSessionFormValues, type RCNPDSeriesSession } from '../../../../../models/RCNPDSeriesSession'
import { isValidPositiveNumber } from '../../../../../utils/isValidNumber'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { BudgetThresholdExceededModal } from '../../BudgetExceededModal'
import { budgetNeedsReview } from '../../../../../utils/budgetUtil'
import { handleApiResponse } from '../../../../../utils/apiHelpers'
import { isValidTrainingLength } from '../../../../../utils/trainingLength'
import { SESSION_DATES_HELPER_TEXT, TRAINING_DATE_HELPER_TEXT } from '../../../../../constants/trainingConstants'

enum TrainingFormatOptions {
  InPerson = 'In-Person',
  Virtual = 'Virtual',
  Hybrid = 'Hybrid',
  OnDemand = 'On-Demand'
}

export interface AddTrainingFormValues {
  priorityId: number
  title: string
  organizationIds: number[]
  trainingFormat: string | undefined
  scope: string
  open: boolean | null
  resourcesNeeded: string
  budgetRequest: string
  isSeries: boolean
  contentFocus?: string[]
  trainingDate?: Date | null
  trainingLength?: TrainingLength | null
  isImplementationPhase?: boolean
  trainingHeld?: boolean | null
  numberOfAttendees?: string | null
  numberOfTeamsTrained?: string | null
  actualBudgetAmount?: string | null
  rcnpdId?: number | null
  seriesSessions?: AddRCNPDSeriesSessionFormValues[]
  contractSessions?: AddRCNPDContractSessionFormValues[]
}

export interface UpdateTrainingFormValues extends AddTrainingFormValues {
  id: number
  isUpdate?: boolean
}

export interface AdditionalDetailsFormValues {
  contentFocus: string[]
  trainingDate: Date | null
  trainingLength: TrainingLength | null
  otherContentFocus?: string | null
}

const MENU_ITEM_HEIGHT = 48
const MENU_ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: MENU_ITEM_HEIGHT * 4.5 + MENU_ITEM_PADDING_TOP,
      width: 250
    }
  }
}

const useFormValues = (initialValues?: UpdateTrainingFormValues, priorityId?: number, isSeries?: boolean, implementationPhase?: boolean): {
  formValues: AddTrainingFormValues
  setFormValues: Dispatch<SetStateAction<AddTrainingFormValues>>
  formValidators: Record<keyof UpdateTrainingFormValues, { required: boolean, isValid: boolean }>
  addSeriesSession: (session?: RCNPDSeriesSession) => AddRCNPDSeriesSessionFormValues
  addContractSession: (session?: AddRCNPDContractSessionFormValues) => AddRCNPDContractSessionFormValues
} => {
  const addSeriesSession = (session?: RCNPDSeriesSession): AddRCNPDSeriesSessionFormValues => ({
    id: session?.id ?? 0,
    rcnpdUpdateId: session?.rcnpdUpdateId ?? initialValues?.id ?? 0,
    name: session?.name ?? '',
    contentFocus: session?.contentFocus ?? [],
    date: session?.date ?? null,
    length: session?.length ?? '',
    trainingHeld: session?.trainingHeld,
    numberOfAttendees: session?.numberOfAttendees ?? null,
    numberOfTeamsTrained: session?.numberOfTeamsTrained ?? null,
    budget: session?.budget ?? null,
    budgetSpent: session?.budgetSpent ?? null
  })

  const addContractSession = (session?: AddRCNPDContractSessionFormValues): AddRCNPDContractSessionFormValues => ({
    id: session?.id ?? 0,
    name: session?.name ?? '',
    date: session?.date ?? null
  })

  // When entering Updates phase for the first time on a series that has contract-phase
  // sessions but no series sessions yet, seed the series sessions with the contract Name+Date
  // so the user does not have to re-enter that data.
  const initialSeriesSessions = (): AddRCNPDSeriesSessionFormValues[] | undefined => {
    if (isSeries !== true) return undefined
    if (initialValues?.seriesSessions != null && initialValues.seriesSessions.length > 0) {
      return initialValues.seriesSessions
    }
    if (
      implementationPhase === true &&
      initialValues?.contractSessions != null &&
      initialValues.contractSessions.length > 0
    ) {
      return initialValues.contractSessions.map(cs => {
        const seededSession: RCNPDSeriesSession = {
          id: 0,
          rcnpdUpdateId: initialValues?.id ?? 0,
          name: cs.name,
          contentFocus: [],
          date: cs.date,
          length: ''
        }
        return addSeriesSession(seededSession)
      })
    }
    return [addSeriesSession()]
  }

  const initialContractSessions = (): AddRCNPDContractSessionFormValues[] | undefined => {
    if (isSeries !== true || implementationPhase === true) return undefined
    if (initialValues?.contractSessions != null && initialValues.contractSessions.length > 0) {
      return initialValues.contractSessions.map(cs => addContractSession(cs))
    }
    return [addContractSession()]
  }

  const [formValues, setFormValues] = useState<AddTrainingFormValues>({
    priorityId: priorityId ?? 0,
    title: '',
    organizationIds: [],
    trainingFormat: undefined,
    scope: '',
    open: null,
    resourcesNeeded: '',
    budgetRequest: '',
    isSeries: isSeries ?? false,
    isImplementationPhase: initialValues?.isImplementationPhase,
    contentFocus: initialValues?.contentFocus,
    trainingDate: initialValues?.trainingDate,
    trainingLength: initialValues?.trainingLength,
    seriesSessions: initialSeriesSessions(),
    contractSessions: initialContractSessions()
  })

  useEffect(() => {
    if (initialValues != null) {
      setFormValues({
        ...initialValues,
        seriesSessions: initialSeriesSessions(),
        contractSessions: initialContractSessions()
      })
    }
  }, [initialValues, implementationPhase])

  const formValidators: Record<keyof UpdateTrainingFormValues, { required: boolean, isValid: boolean }> = {
    id: { required: initialValues != null, isValid: (initialValues?.id ?? 0) > 0 },
    rcnpdId: { required: false, isValid: true },
    priorityId: { required: true, isValid: formValues.priorityId > 0 },
    title: { required: true, isValid: formValues.title !== '' },
    organizationIds: { required: true, isValid: formValues.organizationIds.length > 0 },
    trainingFormat: { required: false, isValid: true },
    scope: { required: true, isValid: formValues.scope !== '' },
    open: { required: false, isValid: true },
    resourcesNeeded: { required: true, isValid: formValues.resourcesNeeded !== '' },
    budgetRequest: { required: true, isValid: !isNaN(Number(formValues.budgetRequest)) && Number(formValues.budgetRequest) >= 0 },
    isSeries: { required: true, isValid: true },
    contentFocus: { required: false, isValid: true },
    trainingDate: { required: false, isValid: true },
    trainingLength: { required: false, isValid: true },
    isImplementationPhase: { required: false, isValid: true },
    trainingHeld: { required: false, isValid: true },
    numberOfAttendees: { required: false, isValid: true },
    numberOfTeamsTrained: { required: false, isValid: true },
    actualBudgetAmount: { required: false, isValid: true },
    isUpdate: { required: false, isValid: true },
    seriesSessions: { required: initialValues?.isSeries === true, isValid: (formValues.seriesSessions?.length ?? 0) > 0 },
    contractSessions: { required: false, isValid: true }
  }

  return {
    formValues,
    setFormValues,
    formValidators,
    addSeriesSession,
    addContractSession
  }
}

const useAdditionalDetailsFormValues = (initialValues?: UpdateTrainingFormValues): {
  formValues: AdditionalDetailsFormValues
  setFormValues: Dispatch<SetStateAction<AdditionalDetailsFormValues>>
  otherContentFocusSelected: boolean
  handleFieldUpdate: (e: { target: { name: string, value: string } }) => void
} => {
  const [formValues, setFormValues] = useState<AdditionalDetailsFormValues>({
    contentFocus: [],
    trainingDate: null,
    trainingLength: null,
    otherContentFocus: null
  })

  useEffect(() => {
    if (initialValues != null) {
      setFormValues({
        contentFocus: initialValues.contentFocus ?? [],
        trainingDate: initialValues.trainingDate ?? null,
        trainingLength: initialValues.trainingLength ?? null
      })
    }
  }, [initialValues])

  const handleFieldUpdate = (e: { target: { name: string, value: string } }): void => {
    const { name, value } = e.target
    setFormValues({ ...formValues, [name]: value })
  }

  const otherContentFocusSelected = formValues.contentFocus?.includes(ContentFocus.Other)

  return {
    formValues,
    setFormValues,
    otherContentFocusSelected,
    handleFieldUpdate
  }
}

const useOrganizationOptions = (rcnId: number): OrganizationOption[] => {
  const [isdDistrictOptions, setIsdDistrictOptions] = useState<OrganizationOption[]>([])

  useEffect(() => {
    const getOrgOptions = async (): Promise<void> => {
      const isdDistrictOptions = await handleApiResponse(GetIsdsAndDistrictsForPlanOrganization(rcnId))
      if (isdDistrictOptions != null) setIsdDistrictOptions(isdDistrictOptions)
    }

    void getOrgOptions()
  }, [rcnId])

  return isdDistrictOptions
}

interface Props {
  isSeries?: boolean
  open: boolean
  onClose: (isUpdate?: boolean) => void
  onSaved: () => void
  initialValues?: UpdateTrainingFormValues
  planOrganizationId: number
  priorityId: number
  implementationPhase?: boolean
  startOnDetailsPage?: boolean
}

type ConfirmState = 'none' | 'needsConfirmation' | 'confirmed'

export const AddEditTrainingModal = (props: Props): JSX.Element => {
  const [confirmState, setConfirmState] = useState<ConfirmState>('none')
  const { formValues, setFormValues, addSeriesSession, addContractSession } = useFormValues(props.initialValues, props.priorityId, props.isSeries, props.implementationPhase)
  const isdDistrictOptions = useOrganizationOptions(props.planOrganizationId)
  const [submitted, setSubmitted] = useState(false)
  const [additionalDetailsSubmitted, setAdditionalDetailsSubmitted] = useState(false)
  const [modalPage, setModalPage] = useState<'edit' | 'details'>(props.startOnDetailsPage === true ? 'details' : 'edit')
  const {
    formValues: additionalDetailsFormValues,
    setFormValues: setAdditionalDetailsFormValues,
    otherContentFocusSelected,
    handleFieldUpdate: handleAdditionalDetailsFieldUpdate
  } = useAdditionalDetailsFormValues(props.initialValues)
  const [showCancelUpdatesModal, setShowCancelUpdatesModal] = useState<boolean>(false)

  const handleFieldUpdate = (e: { target: { name: string, value: string } }): void => {
    const { name, value } = e.target
    setFormValues({ ...formValues, [name]: value })
  }

  const handleContractTrainingDateChanged = (newValue: Date | null): void => {
    setFormValues({ ...formValues, trainingDate: newValue ?? null })
  }

  interface ContractSessionFieldEvent {
    target: { name: string, value: string | Date | null }
  }
  const handleContractSessionFieldUpdate = (index: number, e: ContractSessionFieldEvent): void => {
    const { name, value } = e.target
    if (formValues.contractSessions?.[index] == null) return
    const updated = [...formValues.contractSessions]
    updated[index] = { ...updated[index], [name]: value }
    setFormValues({ ...formValues, contractSessions: updated })
  }
  const handleAddContractSession = (): void => {
    const updated = [...(formValues.contractSessions ?? []), addContractSession()]
    setFormValues({ ...formValues, contractSessions: updated })
  }
  const handleRemoveContractSession = (index: number): void => {
    if (formValues.contractSessions == null) return
    const updated = [...formValues.contractSessions]
    updated.splice(index, 1)
    setFormValues({ ...formValues, contractSessions: updated })
  }

  const handleContractSubmit = async (): Promise<void> => {
    setSubmitted(true)

    const formValidators: Record<keyof AddTrainingFormValues, { required: boolean, isValid: boolean }> = {
      priorityId: { required: true, isValid: formValues.priorityId === props.priorityId },
      title: { required: true, isValid: formValues.title.trim() !== '' },
      organizationIds: { required: true, isValid: formValues.organizationIds.length > 0 },
      trainingFormat: { required: true, isValid: formValues.trainingFormat != null && Object.values(TrainingFormatOptions).includes(formValues.trainingFormat as TrainingFormatOptions) },
      scope: { required: true, isValid: formValues.scope.trim() !== '' && Object.values(['Individual', 'Team-based']).includes(formValues.scope) },
      open: { required: true, isValid: typeof formValues.open === 'boolean' || (formValues.open === 'true' || formValues.open === 'false') },
      resourcesNeeded: { required: true, isValid: formValues.resourcesNeeded.trim() !== '' },
      budgetRequest: { required: true, isValid: isValidPositiveNumber(formValues.budgetRequest) },
      isSeries: { required: true, isValid: formValues.isSeries === (props.isSeries ?? false) },
      contentFocus: { required: false, isValid: true },
      trainingDate: { required: false, isValid: true },
      trainingLength: { required: false, isValid: true },
      isImplementationPhase: { required: false, isValid: true },
      trainingHeld: { required: false, isValid: true },
      numberOfAttendees: { required: false, isValid: true },
      numberOfTeamsTrained: { required: false, isValid: true },
      actualBudgetAmount: { required: false, isValid: true },
      rcnpdId: { required: false, isValid: true },
      seriesSessions: { required: false, isValid: true },
      contractSessions: { required: false, isValid: true }
    }
    const hasInvalidFormValues = Object.values(formValidators).some(validator => validator.required && !validator.isValid)

    if (hasInvalidFormValues) {
      toast.error('Please fill out required fields')
      return
    }

    const submitValues: AddTrainingFormValues = {
      ...formValues,
      // single-event date is only meaningful for non-series; for series, the per-session dates win
      trainingDate: formValues.isSeries ? null : formValues.trainingDate ?? null,
      contractSessions: formValues.isSeries
        ? (formValues.contractSessions ?? []).filter(s => s.name.trim() !== '' || s.date != null)
        : undefined
    }

    const { success, errors } = props.initialValues != null
      ? await UpdateRCNPD({ ...submitValues, id: props.initialValues.id })
      : await AddRCNPD(submitValues)

    if (!success) {
      errors.forEach((error) => toast.error(error))
      return
    }

    toast.success(props?.isSeries === true ? 'Training Series Saved' : 'Single Training Event Saved')

    props.onClose(props.initialValues?.isUpdate ?? false)
    props.onSaved()
  }

  const saveUpdates = async (isUpdate?: boolean, saveSeriesSessions: boolean = false): Promise<boolean> => {
    if (props.implementationPhase !== true) return false

    let savePromise: Promise<Response> | null = null
    // Handle "Other" content focus option
    if (additionalDetailsSubmitted) {
      if (props.isSeries === true) {
        formValues.seriesSessions = formValues.seriesSessions?.map(session => {
          const contentFocus = session.contentFocus?.filter(cf => cf !== ContentFocus.Other) ?? []
          const trimmedOtherContentFocus = session.otherContentFocus?.trim() ?? ''
          if (trimmedOtherContentFocus !== '') contentFocus.push(trimmedOtherContentFocus)

          return { ...session, contentFocus }
        })
      } else {
        additionalDetailsFormValues.contentFocus = additionalDetailsFormValues.contentFocus?.filter(cf => cf !== ContentFocus.Other) ?? []
        const trimmedOtherContentFocus = additionalDetailsFormValues.otherContentFocus?.trim() ?? ''
        if (trimmedOtherContentFocus !== '') additionalDetailsFormValues.contentFocus.push(trimmedOtherContentFocus)
      }
    }

    if (props.initialValues == null) {
      const rcnPdUpdate: AddTrainingFormValues = {
        ...formValues,
        seriesSessions: saveSeriesSessions ? formValues.seriesSessions : undefined,
        isImplementationPhase: props.implementationPhase ?? false,
        contentFocus: props.isSeries === true ? undefined : additionalDetailsFormValues.contentFocus,
        trainingDate: props.isSeries === true ? undefined : additionalDetailsFormValues.trainingDate,
        trainingLength: props.isSeries === true ? undefined : additionalDetailsFormValues.trainingLength,
        budgetRequest: formValues.budgetRequest
      }

      savePromise = AddRCNPD(rcnPdUpdate)
    } else {
      const rcnPdUpdate: UpdateTrainingFormValues = {
        ...formValues,
        seriesSessions: saveSeriesSessions ? formValues.seriesSessions : undefined,
        id: props.initialValues.id,
        isImplementationPhase: props.implementationPhase ?? false,
        isUpdate,
        budgetRequest: formValues.budgetRequest,
        contentFocus: props.isSeries === true ? undefined : additionalDetailsFormValues.contentFocus,
        trainingDate: props.isSeries === true ? undefined : additionalDetailsFormValues.trainingDate,
        trainingLength: props.isSeries === true ? undefined : additionalDetailsFormValues.trainingLength
      }

      savePromise = UpdateRCNPD(rcnPdUpdate)
    }

    if (savePromise == null) return false

    const { success, errors } = await savePromise

    if (!success) {
      errors.forEach((error) => toast.error(error))
      return false
    }
    return true
  }

  const sessionUpdateFormValidators = (session: AddRCNPDSeriesSessionFormValues): Record<keyof AddRCNPDSeriesSessionFormValues, { required: boolean, isValid: boolean }> => {
    const otherContentFocusSelected: boolean = session.contentFocus.includes(ContentFocus.Other)
    const validOtherContentFocus = otherContentFocusSelected ? (session.otherContentFocus != null && session.otherContentFocus.trim() !== '') : true
    const validators = {
      id: { required: false, isValid: true },
      rcnpdUpdateId: { required: props.initialValues != null, isValid: session.rcnpdUpdateId > 0 && session.rcnpdUpdateId === props.initialValues?.id },
      name: { required: true, isValid: session.name.trim() !== '' },
      contentFocus: { required: true, isValid: session.contentFocus.length > 0 },
      date: { required: true, isValid: session.date != null && new Date(session.date) instanceof Date && !isNaN(new Date(session.date).getTime()) },
      length: { required: true, isValid: isValidTrainingLength(session.length) },
      trainingHeld: { required: false, isValid: session.trainingHeld != null && (typeof session.trainingHeld === 'boolean' || (session.trainingHeld === 'true' || session.trainingHeld === 'false')) },
      numberOfAttendees: { required: false, isValid: session.numberOfAttendees != null && (session.numberOfAttendees === '' || (!isNaN(Number(session.numberOfAttendees)) && Number(session.numberOfAttendees) >= 0)) },
      numberOfTeamsTrained: { required: false, isValid: session.numberOfTeamsTrained != null && (session.numberOfTeamsTrained === '' || (!isNaN(Number(session.numberOfTeamsTrained)) && Number(session.numberOfTeamsTrained) >= 0)) },
      budget: { required: false, isValid: session.budget != null && (session.budget === '' || (!isNaN(Number(session.budget)) && Number(session.budget) >= 0)) },
      budgetSpent: { required: false, isValid: session.budgetSpent != null && (session.budgetSpent === '' || (!isNaN(Number(session.budgetSpent)) && Number(session.budgetSpent) >= 0)) },
      otherContentFocus: { required: otherContentFocusSelected, isValid: validOtherContentFocus }
    }
    return validators
  }
  const hasInvalidSessionFormValues = (session: AddRCNPDSeriesSessionFormValues): boolean => {
    return Object.entries(session).some(([key, value]) => {
      const validator = sessionUpdateFormValidators(session)[key as keyof AddRCNPDSeriesSessionFormValues]
      return validator.required && !validator.isValid
    })
  }

  const handleSubmitAdditionalDetails = async (): Promise<void> => {
    setAdditionalDetailsSubmitted(true)

    if (props.isSeries === true) {
      if (
        formValues.seriesSessions == null ||
        formValues.seriesSessions.length === 0
      ) {
        toast.error('Please add at least one training session')
        return
      }
      if (formValues.seriesSessions.some(hasInvalidSessionFormValues)) {
        toast.error('Please fill out required fields in all training sessions')
        return
      }
    } else {
      const formValidators: Record<keyof AdditionalDetailsFormValues, { required: boolean, isValid: boolean }> = {
        contentFocus: { required: true, isValid: additionalDetailsFormValues.contentFocus.length > 0 },
        trainingDate: { required: true, isValid: additionalDetailsFormValues.trainingDate != null },
        trainingLength: { required: true, isValid: isValidTrainingLength(additionalDetailsFormValues.trainingLength) },
        otherContentFocus: {
          required: false,
          isValid: additionalDetailsFormValues.contentFocus?.includes(ContentFocus.Other)
            ? (additionalDetailsFormValues.otherContentFocus != null && additionalDetailsFormValues.otherContentFocus.trim() !== '')
            : true
        }
      }
      if (Object.values(formValidators).some(validator => validator.required && !validator.isValid)) {
        toast.error('Please fill out required additional details fields')
        return
      }
    }

    const savedUpdates = await saveUpdates(props.initialValues?.isUpdate ?? false, true)
    if (!savedUpdates) return
    props.onClose(props.initialValues?.isUpdate ?? false)
    props.onSaved()
    toast.success(`${props.isSeries === true ? 'Training Series' : 'Single Training Event'} Updated`)
  }

  const handleImplementationSubmit = async (): Promise<void> => {
    if (!isValidPositiveNumber(formValues.budgetRequest)) {
      toast.error('Budget must be greater than or equal to 0')
      return
    }

    if (props.initialValues != null && modalPage === 'edit' && props.initialValues.isUpdate !== true) {
      if (budgetNeedsReview(props.initialValues.budgetRequest, formValues.budgetRequest)) {
        setConfirmState('needsConfirmation')
        return
      }
    }

    await continueImplementationSubmit()
  }

  const continueImplementationSubmit = async (): Promise<void> => {
    if (modalPage === 'edit') {
      setSubmitted(true)

      // form validator for implementation phase edit page
      const formValidators: Record<keyof UpdateTrainingFormValues, { required: boolean, isValid: boolean }> = {
        id: { required: props.initialValues != null, isValid: (props.initialValues?.id ?? 0) > 0 },
        priorityId: { required: true, isValid: formValues.priorityId > 0 },
        title: { required: true, isValid: formValues.title.trim() !== '' },
        organizationIds: { required: true, isValid: formValues.organizationIds.length > 0 },
        trainingFormat: { required: true, isValid: Object.values(TrainingFormatOptions).includes(formValues.trainingFormat as TrainingFormatOptions) },
        scope: { required: true, isValid: Object.values(['Individual', 'Team-based']).includes(formValues.scope) },
        open: { required: true, isValid: (typeof formValues.open === 'boolean' || (formValues.open === 'true' || formValues.open === 'false')) },
        resourcesNeeded: { required: true, isValid: formValues.resourcesNeeded.trim() !== '' },
        budgetRequest: { required: true, isValid: isValidPositiveNumber(formValues.budgetRequest) },
        isSeries: { required: true, isValid: typeof formValues.isSeries === 'boolean' },
        isImplementationPhase: { required: true, isValid: true }, // handled in the saveUpdates function
        isUpdate: { required: true, isValid: true }, // handled in the saveUpdates function
        contentFocus: { required: false, isValid: true },
        trainingDate: { required: false, isValid: true },
        trainingLength: { required: false, isValid: true },
        trainingHeld: { required: false, isValid: true },
        numberOfAttendees: { required: false, isValid: true },
        numberOfTeamsTrained: { required: false, isValid: true },
        actualBudgetAmount: { required: false, isValid: true },
        rcnpdId: { required: false, isValid: true },
        seriesSessions: { required: false, isValid: true },
        contractSessions: { required: false, isValid: true }
      }

      const invalidFormValues = Object.entries(formValidators)
        .filter(([_, validator]) => validator.required && !validator.isValid)
        .map(([fieldName, _]) => fieldName)

      if (invalidFormValues.includes('budgetRequest')) {
        if (!isValidPositiveNumber(formValues.budgetRequest)) {
          toast.error('Budget must be greater than or equal to 0')
          return
        }
      } else if (invalidFormValues.length > 0) {
        toast.error('Please fill out required fields')
        return
      }

      // navigate to details page of modal
      setModalPage('details')
    } else if (modalPage === 'details') {
      // Save updates and close with notification
      void handleSubmitAdditionalDetails()
    }
  }

  const handleSubmit = (): void => {
    if (props.implementationPhase === true) {
      void handleImplementationSubmit()
    } else {
      void handleContractSubmit()
    }
  }

  const handleConfirmCancelUpdates = async (): Promise<void> => {
    setShowCancelUpdatesModal(false)
    setSubmitted(true)
    // form validator for implementation phase edit page
    const formValidators: Record<keyof UpdateTrainingFormValues, { required: boolean, isValid: boolean }> = {
      id: { required: props.initialValues != null, isValid: (props.initialValues?.id ?? 0) > 0 },
      priorityId: { required: true, isValid: formValues.priorityId > 0 },
      title: { required: true, isValid: formValues.title.trim() !== '' },
      organizationIds: { required: true, isValid: formValues.organizationIds.length > 0 },
      trainingFormat: { required: true, isValid: Object.values(TrainingFormatOptions).includes(formValues.trainingFormat as TrainingFormatOptions) },
      scope: { required: true, isValid: Object.values(['Individual', 'Team-based']).includes(formValues.scope) },
      open: { required: true, isValid: typeof formValues.open === 'boolean' },
      resourcesNeeded: { required: true, isValid: formValues.resourcesNeeded.trim() !== '' },
      budgetRequest: { required: true, isValid: isValidPositiveNumber(formValues.budgetRequest) },
      isSeries: { required: true, isValid: typeof formValues.isSeries === 'boolean' },
      isImplementationPhase: { required: true, isValid: true }, // handled in the saveUpdates function
      isUpdate: { required: true, isValid: true }, // handled in the saveUpdates function
      contentFocus: { required: false, isValid: true },
      trainingDate: { required: false, isValid: true },
      trainingLength: { required: false, isValid: true },
      trainingHeld: { required: false, isValid: true },
      numberOfAttendees: { required: false, isValid: true },
      numberOfTeamsTrained: { required: false, isValid: true },
      actualBudgetAmount: { required: false, isValid: true },
      rcnpdId: { required: false, isValid: true },
      seriesSessions: { required: false, isValid: true },
      contractSessions: { required: false, isValid: true }
    }

    const invalidFormValues = Object.entries(formValidators)
      .filter(([_, validator]) => validator.required && !validator.isValid)
      .map(([fieldName, _]) => fieldName)

    if (invalidFormValues.includes('budgetRequest')) {
      if (!isValidPositiveNumber(formValues.budgetRequest)) {
        toast.error('Budget must be greater than or equal to 0')
        return
      }
    } else if (invalidFormValues.length > 0) {
      toast.error('Please fill out required fields')
      return
    }

    const savedUpdates = await saveUpdates(props.initialValues?.isUpdate ?? false)
    if (savedUpdates) {
      toast.success(`${formValues.isSeries ? 'Training Series' : 'Single Training Event'} Partially Updated`)
      props.onClose(props.initialValues?.isUpdate ?? false)
      props.onSaved()
    }
  }

  const handleClose = async (): Promise<void> => {
    if (props.implementationPhase === true && modalPage === 'details') {
      setShowCancelUpdatesModal(true)
    } else {
      props.onClose(props.initialValues?.isUpdate ?? false)
    }
  }

  const handleCancel = (): void => {
    if (props.implementationPhase === true) {
      if (modalPage === 'edit') props.onClose(props.initialValues?.isUpdate ?? false)
      else if (modalPage === 'details') {
        setModalPage('edit')
      }
    } else {
      props.onClose(props.initialValues?.isUpdate ?? false)
    }
  }

  const handleContentFocusChange = (e: SelectChangeEvent<typeof additionalDetailsFormValues.contentFocus>): void => {
    const value = e.target.value as ContentFocus[]
    const updatedAdditionalDetailsFormValues = { ...additionalDetailsFormValues, contentFocus: value }
    // Clear otherContentFocus if "Other" is not selected
    if (!value.includes(ContentFocus.Other)) {
      updatedAdditionalDetailsFormValues.otherContentFocus = null
    }
    setAdditionalDetailsFormValues(updatedAdditionalDetailsFormValues)
  }

  const handleDateChanged = (newValue: Date | null): void => {
    setAdditionalDetailsFormValues({
      ...additionalDetailsFormValues,
      trainingDate: newValue ?? null
    })
  }

  const renderOrgNames = (orgIds: number[]): string => {
    return orgIds.map((orgId) => {
      const org = isdDistrictOptions.find((option) => option.id === orgId)
      return org?.name ?? ''
    }).join(', ')
  }

  const getTitle = (): string => {
    if (props.initialValues != null) {
      return `${props.implementationPhase === true ? 'Update' : 'Edit'} ${props?.isSeries === true ? 'Training Series' : 'Single Training Event'}`
    }
    return `Add ${props?.isSeries === true ? 'Training Series' : 'Single Training Event'}`
  }

  const handleAddSession = (): void => {
    const updatedFormValues = { ...formValues }
    const newSession = addSeriesSession()
    Array.isArray(updatedFormValues.seriesSessions)
      ? updatedFormValues.seriesSessions.push(newSession)
      : updatedFormValues.seriesSessions = [newSession]
    setFormValues(updatedFormValues)
  }

  interface SessionFieldEvent {
    target: { name: string, value: string | string[] | Date | null }
  }
  const handleSessionFieldUpdate = (index: number, e: SessionFieldEvent): void => {
    const { name, value } = e.target
    if (formValues.seriesSessions == null || formValues.seriesSessions[index] == null) return

    const updatedSessions = [...formValues.seriesSessions]
    const sessionToUpdate = { ...updatedSessions[index], [name]: value }
    updatedSessions[index] = sessionToUpdate

    setFormValues({ ...formValues, seriesSessions: updatedSessions })
  }

  const handleRemoveSession = (index: number): void => {
    if (formValues.seriesSessions == null || formValues.seriesSessions[index] == null) return

    const updatedSessions = [...formValues.seriesSessions]
    updatedSessions.splice(index, 1)

    setFormValues({ ...formValues, seriesSessions: updatedSessions })
  }

  const firstSessionOtherContentFocusSelected = (): boolean => {
    if (formValues.seriesSessions == null || formValues.seriesSessions.length === 0) return false

    return formValues.seriesSessions[0].contentFocus.includes(ContentFocus.Other)
  }

  if (showCancelUpdatesModal) {
    return (
      <Modal
        open={true}
        title='Cancel Updates'
        cancelButtonText='Go Back'
        confirmButtonText='Confirm Cancel'
        data-testid='confirmCancelUpdatesModal'
        bodyContent={<Stack gap='1rem' m='24px 0'>
          <Typography variant='body1' fontWeight={600}>Are you sure you want to cancel?</Typography>
          <Typography variant='body1'>If you proceed your changes in the Additional Details section will not be saved.</Typography>
        </Stack>}
        onClose={() => { setShowCancelUpdatesModal(false) }}
        onConfirm={handleConfirmCancelUpdates}
        maxWidth='xs'
      />
    )
  }

  return (confirmState === 'needsConfirmation'
    ? <BudgetThresholdExceededModal
        onClose={() => {
          setConfirmState('none')
          props.onClose(props.initialValues?.isUpdate ?? false)
        }}
        onConfirm={() => {
          setConfirmState('confirmed')
          void continueImplementationSubmit()
        }}
    />
    : <Modal
        title={getTitle()}
        open={props.open}
        data-testid={props?.initialValues != null ? 'editTrainingModal' : 'addTrainingModal'}
        maxWidth="xs"
        confirmButtonText={props.implementationPhase === true ? 'Save & Continue' : 'Save'}
        cancelButtonText={props.implementationPhase === true && modalPage === 'details' ? 'Go Back' : 'Cancel'}
        onClose={handleClose}
        onCancel={handleCancel}
        onConfirm={handleSubmit}
        bodyContent={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', my: '24px' }}>
            {modalPage === 'edit' && (
              <>
                {
                  props.implementationPhase === true && props.initialValues == null &&
                    <Alert data-testid='contributorAlert' icon={<InfoOutlinedIcon fontSize="inherit" sx={{ fill: 'white' }} />} sx={{
                      backgroundColor: 'var(--blue-500)',
                      color: 'white',
                      fill: 'white'
                    }} severity="info"
                    >The addition of this item will prompt a review from the START team.
                    </Alert>
                }

                <Typography variant='h5'>Training Info</Typography>
                <TextField
                  name='title'
                  variant='filled'
                  label={props?.isSeries === true ? 'Series Title' : 'Training Title'}
                  value={formValues.title}
                  data-testid='title'
                  onChange={handleFieldUpdate}
                  required={formValues.title === ''}
                  error={submitted && formValues.title === ''}
                />

                <FormControl
                  variant='filled'
                  required={formValues.organizationIds.length === 0}
                  error={submitted && formValues.organizationIds.length === 0}
                >
                  <InputLabel id="isdDistrictLabel">ISD/District</InputLabel>
                  <Select
                    labelId="isdDistrictLabel"
                    label='ISD/District'
                    id="isdDistrict"
                    name='organizationIds'
                    multiple
                    value={formValues.organizationIds}
                    onChange={handleFieldUpdate as (e: SelectChangeEvent<typeof formValues.organizationIds>) => void}
                    renderValue={renderOrgNames}
                    MenuProps={MenuProps}
                    data-testid='isdDistrict'
                  >
                    {isdDistrictOptions.length === 0 && <Typography variant='body1' textAlign={'center'}>No options</Typography>}
                    {isdDistrictOptions.length > 0 && isdDistrictOptions.map((org) => (
                      <MenuItem key={org.id} value={org.id} data-testid='isdDistrictOption'>
                        <Checkbox checked={formValues.organizationIds.includes(org.id)} />
                        <ListItemText primary={org.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  variant='filled'
                  required={formValues.trainingFormat == null}
                  error={submitted && formValues.trainingFormat == null}
                >
                  <InputLabel id='training-format-label'>Training Format</InputLabel>
                  <Select
                    labelId='training-format-label'
                    name='trainingFormat'
                    label='Training Format'
                    data-testid='trainingFormat'
                    value={formValues.trainingFormat ?? ''}
                    onChange={handleFieldUpdate as (e: SelectChangeEvent<typeof formValues.trainingFormat>) => void}
                  >
                    {Object.values(TrainingFormatOptions).map(trainingFormat => (
                      <MenuItem key={trainingFormat} value={trainingFormat} data-testid='trainingFormatOption'>
                        {trainingFormat}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  data-testid='scope'
                  required={formValues.scope === ''}
                  error={submitted && formValues.scope === ''}
                >
                  <FormLabel>Will this be an individual or team-based training?</FormLabel>
                  <RadioGroup
                    aria-labelledby="scope-radio-buttons-group"
                    name="scope"
                    value={formValues.scope}
                    onChange={handleFieldUpdate}
                  >
                    <FormControlLabel data-testid='scopeIndividual' value="Individual" control={<Radio />} label="Individual" />
                    <FormControlLabel data-testid='scopeTeamBased' value="Team-based" control={<Radio />} label="Team-based" />
                  </RadioGroup>
                </FormControl>

                <FormControl
                  data-testid='open'
                  required={formValues.open == null}
                  error={submitted && formValues.open == null}
                >
                  <FormLabel>If this training is open to other participants may we share it on the START website?</FormLabel>
                  <RadioGroup
                    aria-labelledby="open-radio-buttons-group"
                    name="open"
                    value={formValues.open ?? ''}
                    onChange={handleFieldUpdate}
                  >
                    <FormControlLabel data-testid='openYes' value={true} control={<Radio />} label="Yes" />
                    <FormControlLabel data-testid='openNo' value={false} control={<Radio />} label="No" />
                  </RadioGroup>
                </FormControl>

                <TextField
                  name='resourcesNeeded'
                  label='Resources Needed'
                  value={formValues.resourcesNeeded}
                  data-testid='resourcesNeeded'
                  onChange={handleFieldUpdate}
                  variant="filled"
                  multiline={true}
                  rows={4}
                  required={formValues.resourcesNeeded === ''}
                  error={submitted && formValues.resourcesNeeded === ''}
                />

                <Stack>
                  <Typography variant='body1'>Does this require additional funding to complete?</Typography>
                  <TextField
                    id='budget-request'
                    sx={{ mt: '.75em', width: '250px' }}
                    variant='filled'
                    onChange={handleFieldUpdate}
                    label={'Budget Request'}
                    data-testid='budgetRequest'
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    name='budgetRequest'
                    required
                    error={submitted && formValues.budgetRequest === ''}
                    value={formValues.budgetRequest}
                  />
                </Stack>

                {props.implementationPhase !== true && props.isSeries !== true && (
                  <Stack>
                    <Typography variant='body1'>
                      Training Date
                      <Typography component='span' variant='caption' color='var(--grey-500)' ml='.25em'>Optional</Typography>
                    </Typography>
                    <Typography variant='caption' color='var(--grey-500)'>
                      {TRAINING_DATE_HELPER_TEXT}
                    </Typography>
                    <DatePickerWithLabel
                      FormControlProps={{ variant: 'outlined', sx: { marginTop: '.75em', width: '225px' } }}
                      name='trainingDate'
                      label='Training Date'
                      dataTestId='contractTrainingDate'
                      onChange={handleContractTrainingDateChanged}
                      sx={{ mt: '.75em' }}
                      value={formValues.trainingDate ?? null}
                    />
                  </Stack>
                )}

                {props.implementationPhase !== true && props.isSeries === true && (
                  <Stack gap='.5rem'>
                    <Typography variant='body1'>
                      Session Dates
                      <Typography component='span' variant='caption' color='var(--grey-500)' ml='.25em'>Optional</Typography>
                    </Typography>
                    <Typography variant='caption' color='var(--grey-500)'>
                      {SESSION_DATES_HELPER_TEXT}
                    </Typography>
                    {(formValues.contractSessions ?? []).map((session, index) => (
                      <Stack
                        key={`contract-session-${index}`}
                        gap='.75rem'
                        sx={{
                          border: '1px solid rgba(225, 225, 226, 1)',
                          borderRadius: '8px',
                          padding: '.75rem'
                        }}
                      >
                        <TextField
                          name='name'
                          variant='filled'
                          label='Session Name'
                          value={session.name}
                          data-testid={`contractSessionName-${index}`}
                          onChange={(e) => { handleContractSessionFieldUpdate(index, e) }}
                        />
                        <DatePickerWithLabel
                          FormControlProps={{ variant: 'outlined', sx: { width: '225px' } }}
                          name='date'
                          label='Session Date'
                          dataTestId={`contractSessionDate-${index}`}
                          onChange={(e) => { handleContractSessionFieldUpdate(index, { target: { name: 'date', value: e } }) }}
                          value={session.date ?? null}
                        />
                        {(formValues.contractSessions?.length ?? 0) > 1 && (
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                              size='small'
                              sx={{ width: 'fit-content', color: 'var(--black)' }}
                              data-testid={`removeContractSession-${index}`}
                              onClick={() => { handleRemoveContractSession(index) }}
                            >
                              Remove Session
                              <RemoveCircle sx={{ ml: '.5rem', color: 'var(--red-700)' }} />
                            </Button>
                          </Box>
                        )}
                      </Stack>
                    ))}
                    <Button
                      sx={{
                        width: 'fit-content',
                        color: 'var(--black)',
                        fontWeight: 600,
                        fontSize: '1rem'
                      }}
                      data-testid='addContractSession'
                      onClick={handleAddContractSession}
                    >
                      Add Session
                      <AddCircle sx={{ ml: '.5rem', color: 'var(--blue-700)' }} />
                    </Button>
                  </Stack>
                )}

                {props.initialValues != null &&
                  <ActivityEditSession
                    activityId={props.initialValues.id}
                    activityName={PriorityOneActivityName.RcnProvidedProfessionalDevelopment}
                    onClose={() => { props.onClose(props.initialValues?.isUpdate ?? false) }}
                  />
                }
              </>
            )}

            {modalPage === 'details' && (
              <>
                <Typography variant='h4'>Additional Details</Typography>
                {props.isSeries === true
                  ? (
                    <>
                      <Typography variant='body1'>Add the details of your training series sessions below:</Typography>

                      {formValues.seriesSessions == null || formValues.seriesSessions.length < 2
                        ? (
                          <>
                            <TextField
                              required
                              name='name'
                              variant='filled'
                              label='Name of Session'
                              value={formValues.seriesSessions?.[0]?.name ?? ''}
                              data-testid='sessionName-0'
                              onChange={(event) => { handleSessionFieldUpdate(0, event) }}
                              error={additionalDetailsSubmitted && formValues.seriesSessions?.[0]?.name === ''}
                            />

                            <FormControl
                              required
                              variant='filled'
                              error={additionalDetailsSubmitted && (formValues.seriesSessions?.[0]?.contentFocus == null || formValues.seriesSessions?.[0]?.contentFocus.length === 0)}
                            >
                              <InputLabel id='contentFocusLabel-0'>Select Content Focus</InputLabel>
                              <Select
                                multiple
                                labelId='contentFocusLabel-0'
                                label='Select Content Focus'
                                id='contentFocus-0'
                                name='contentFocus'
                                value={formValues.seriesSessions?.[0]?.contentFocus ?? []}
                                onChange={(e: SelectChangeEvent<string[]>) => { handleSessionFieldUpdate(0, e) }}
                                MenuProps={MenuProps}
                                data-testid='contentFocus-0'
                                renderValue={(selected) => (selected).join(', ')}
                              >
                                {Array.from(new Set([...Object.values(ContentFocus), ...(formValues.seriesSessions?.[0]?.contentFocus ?? [])]))
                                  .map(contentFocus => (
                                    <MenuItem key={contentFocus} value={contentFocus} data-testid='sessionContentFocusOption'>
                                      <Checkbox checked={formValues.seriesSessions?.[0]?.contentFocus?.includes(contentFocus)} />
                                      <ListItemText primary={contentFocus} />
                                    </MenuItem>
                                  ))
                                }
                              </Select>
                            </FormControl>

                            {firstSessionOtherContentFocusSelected() && (
                              <TextField
                                required
                                error={additionalDetailsSubmitted && formValues.seriesSessions?.[0]?.otherContentFocus?.trim() === ''}
                                variant="filled"
                                label='Please specify other content focus'
                                name='otherContentFocus'
                                data-testid='otherContentFocus-0'
                                value={formValues.seriesSessions?.[0]?.otherContentFocus ?? ''}
                                onChange={(e) => { handleSessionFieldUpdate(0, e) }}
                              />
                            )}

                            <DatePickerWithLabel
                              required
                              disablePast={props.implementationPhase !== true}
                              FormControlProps={{ variant: 'outlined', sx: { marginTop: '.75em', width: '225px' } }}
                              name='date'
                              label='Session Date'
                              error={additionalDetailsSubmitted && formValues.seriesSessions?.[0]?.date == null}
                              dataTestId='sessionDate-0'
                              onChange={(e) => {
                                const event = { target: { name: 'date', value: e } }
                                handleSessionFieldUpdate(0, event)
                              }}
                              sx={{ mt: '.75em' }}
                              value={formValues.seriesSessions?.[0]?.date ?? null}
                            />

                            <FormControl
                              variant='filled'
                              required={formValues.seriesSessions?.[0]?.length === ''}
                              error={additionalDetailsSubmitted && formValues.seriesSessions?.[0]?.length === ''}
                            >
                              <InputLabel id='sessionLengthLabel-0'>Select Session Length</InputLabel>
                              <Select
                                labelId='sessionLengthLabel-0'
                                name='length'
                                label='Select Session Length'
                                data-testid='sessionLength-0'
                                value={formValues.seriesSessions?.[0]?.length ?? ''}
                                onChange={(e: SelectChangeEvent<string>) => { handleSessionFieldUpdate(0, e) }}
                              >
                                {Object.values(TrainingLength).map(trainingLength => (
                                  <MenuItem key={trainingLength} value={trainingLength} data-testid='sessionLengthOption'>
                                    {trainingLength}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>

                            <Stack>
                              <Typography variant='body1' color='var(--grey-500)' fontStyle='italic'>Optional</Typography>
                              <TextField
                                required
                                id='sessionBudget-0'
                                sx={{ mt: '.75em', width: '250px' }}
                                variant='filled'
                                onChange={(e) => { handleSessionFieldUpdate(0, e) }}
                                label='Budget per Session'
                                data-testid='sessionBudget-0'
                                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                name='budget'
                                error={additionalDetailsSubmitted && formValues.seriesSessions?.[0]?.budget != null && (isNaN(Number(formValues.seriesSessions?.[0]?.budget)) || Number(formValues.seriesSessions?.[0]?.budget) < 0)}
                                value={formValues.seriesSessions?.[0]?.budget ?? ''}
                              />
                            </Stack>
                          </>
                          )
                        : (
                          <>
                            {formValues.seriesSessions?.map((session, index) => {
                              const sessionOtherContentFocusSelected: boolean = session.contentFocus.includes(ContentFocus.Other)
                              const isValid = !hasInvalidSessionFormValues(session)
                              return (
                                <Stack
                                  key={index}
                                  gap='1rem'
                                  sx={{
                                    border: '1px solid rgba(225, 225, 226, 1)',
                                    borderRadius: '8px'
                                  }}
                                >
                                  <Accordion>
                                    <AccordionSummary
                                      expandIcon={<ExpandMore />}
                                      aria-controls={`sessionAccordionContent-${index}`}
                                      id={`sessionAccordionHeader-${index}`}
                                    >
                                      <Typography component="span" variant='h5' fontSize='18px' color={theme => additionalDetailsSubmitted && !isValid ? theme.palette.error.main : 'var(--black)'}>
                                        {session.name === '' ? 'Session Name' : session.name}
                                      </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ pr: '40px', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                      <TextField
                                        name='name'
                                        variant='filled'
                                        label='Name of Session'
                                        value={session.name}
                                        data-testid={`sessionName-${index}`}
                                        onChange={(event) => { handleSessionFieldUpdate(index, event) }}
                                        required={true}
                                        error={additionalDetailsSubmitted && session.name === ''}
                                      />

                                      <FormControl
                                        variant='filled'
                                        required={true}
                                        error={additionalDetailsSubmitted && (session.contentFocus == null || session.contentFocus.length === 0)}
                                      >
                                        <InputLabel id={`contentFocusLabel-${index}`}>Select Content Focus</InputLabel>
                                        <Select
                                          multiple
                                          labelId={`contentFocusLabel-${index}`}
                                          label='Select Content Focus'
                                          id={`contentFocus-${index}`}
                                          name='contentFocus'
                                          value={session.contentFocus ?? []}
                                          onChange={(e: SelectChangeEvent<typeof session.contentFocus>) => { handleSessionFieldUpdate(index, e) }}
                                          MenuProps={MenuProps}
                                          data-testid={`contentFocus-${index}`}
                                          renderValue={(selected) => (selected).join(', ')}
                                        >
                                          {Array.from(new Set([...Object.values(ContentFocus), ...session.contentFocus]))
                                            .map(contentFocus => (
                                              <MenuItem key={contentFocus} value={contentFocus} data-testid='sessionContentFocusOption'>
                                                <Checkbox checked={session.contentFocus.includes(contentFocus)} />
                                                <ListItemText primary={contentFocus} />
                                              </MenuItem>
                                            ))
                                          }
                                        </Select>
                                      </FormControl>

                                      {sessionOtherContentFocusSelected && (
                                        <TextField
                                          required
                                          error={additionalDetailsSubmitted && session?.otherContentFocus?.trim() === ''}
                                          variant="filled"
                                          label='Please specify other content focus'
                                          name='otherContentFocus'
                                          data-testid='otherContentFocus-0'
                                          value={session?.otherContentFocus ?? ''}
                                          onChange={(e) => { handleSessionFieldUpdate(index, e) }}
                                        />
                                      )}

                                      <DatePickerWithLabel
                                        required
                                        disablePast={props.implementationPhase !== true}
                                        FormControlProps={{ variant: 'outlined', sx: { marginTop: '.75em', width: '225px' } }}
                                        name='date'
                                        label='Session Date'
                                        error={additionalDetailsSubmitted && session.date == null}
                                        dataTestId={`sessionDate-${index}`}
                                        onChange={(e) => {
                                          const event = { target: { name: 'date', value: e } }
                                          handleSessionFieldUpdate(index, event)
                                        }}
                                        sx={{ mt: '.75em' }}
                                        value={session.date ?? null}
                                      />

                                      <FormControl
                                        variant='filled'
                                        required={session.length === ''}
                                        error={additionalDetailsSubmitted && session.length === ''}
                                      >
                                        <InputLabel id={`sessionLengthLabel-${index}`}>Select Session Length</InputLabel>
                                        <Select
                                          labelId={`sessionLengthLabel-${index}`}
                                          name='length'
                                          label='Select Session Length'
                                          data-testid={`sessionLength-${index}`}
                                          value={session.length}
                                          onChange={(e: SelectChangeEvent<typeof session.length>) => { handleSessionFieldUpdate(index, e) }}
                                        >
                                          {Object.values(TrainingLength).map(trainingLength => (
                                            <MenuItem key={trainingLength} value={trainingLength} data-testid='sessionLengthOption'>
                                              {trainingLength}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>

                                      <Stack>
                                        <Typography variant='body1' color='var(--grey-500)' fontStyle='italic'>Optional</Typography>
                                        <TextField
                                          required
                                          id={`sessionBudget-${index}`}
                                          sx={{ mt: '.75em', width: '250px' }}
                                          variant='filled'
                                          onChange={(e) => { handleSessionFieldUpdate(index, e) }}
                                          label='Budget per Session'
                                          data-testid={`sessionBudget-${index}`}
                                          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                          name='budget'
                                          error={additionalDetailsSubmitted && session.budget != null && (isNaN(Number(session.budget)) || Number(session.budget) < 0)}
                                          value={session.budget ?? ''}
                                        />
                                      </Stack>

                                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                          size='small'
                                          sx={{ width: 'fit-content', color: 'var(--black)' }}
                                          onClick={() => { handleRemoveSession(index) }}
                                        >
                                          Remove Session
                                          <RemoveCircle sx={{ ml: '.5rem', color: 'var(--red-700)' }} />
                                        </Button>
                                      </Box>
                                    </AccordionDetails>
                                  </Accordion>
                                </Stack>
                              )
                            })}
                          </>
                          )}

                      <Button
                        sx={{
                          width: 'fit-content',
                          color: 'var(--black)',
                          fontWeight: 600,
                          fontSize: '1rem'
                        }}
                        onClick={handleAddSession}
                      >
                        Add Session
                        <AddCircle sx={{ ml: '.5rem', color: 'var(--blue-700)' }} />
                      </Button>
                    </>
                    )
                  : (
                    <>
                      <Typography variant='body1'>Add the details of your training sessions below:</Typography>

                      <FormControl
                        variant='filled'
                        required={additionalDetailsFormValues.contentFocus.length === 0}
                        error={additionalDetailsSubmitted && additionalDetailsFormValues.contentFocus.length === 0}
                      >
                        <InputLabel id='contentFocusLabel'>Select Content Focus</InputLabel>
                        <Select
                          multiple
                          labelId='contentFocusLabel'
                          label='Select Content Focus'
                          name='contentFocus'
                          data-testid='contentFocus'
                          value={additionalDetailsFormValues.contentFocus}
                          onChange={handleContentFocusChange}
                          MenuProps={MenuProps}
                          renderValue={(selected) => (selected).join(', ')}
                        >
                          {Array.from(new Set([...Object.values(ContentFocus), ...additionalDetailsFormValues.contentFocus]))
                            .map(contentFocus => (
                              <MenuItem key={contentFocus} value={contentFocus} data-testid='contentFocusOption'>
                                <Checkbox checked={additionalDetailsFormValues.contentFocus?.includes(contentFocus)} />
                                <ListItemText primary={contentFocus} />
                              </MenuItem>
                            ))
                          }
                        </Select>
                      </FormControl>

                      {otherContentFocusSelected && (
                        <TextField
                          required
                          error={additionalDetailsSubmitted && additionalDetailsFormValues.otherContentFocus?.trim() === ''}
                          variant="filled"
                          label='Please specify other content focus'
                          name='otherContentFocus'
                          data-testid='otherContentFocus'
                          value={additionalDetailsFormValues.otherContentFocus ?? ''}
                          onChange={handleAdditionalDetailsFieldUpdate}
                        />
                      )}

                      <DatePickerWithLabel
                        disablePast={props.implementationPhase !== true}
                        FormControlProps={{ variant: 'outlined', sx: { marginTop: '.75em', width: '225px' } }}
                        name='trainingDate'
                        label='Training Date'
                        required
                        error={additionalDetailsSubmitted && additionalDetailsFormValues.trainingDate == null}
                        dataTestId='trainingDate'
                        onChange={handleDateChanged}
                        sx={{ mt: '.75em' }}
                        value={additionalDetailsFormValues.trainingDate ?? null}
                      />

                      <FormControl
                        variant='filled'
                        required={!isValidTrainingLength(additionalDetailsFormValues.trainingLength)}
                        error={additionalDetailsSubmitted && !isValidTrainingLength(additionalDetailsFormValues.trainingLength)}
                      >
                        <InputLabel id='training-length-label'>Select Training Length</InputLabel>
                        <Select
                          labelId='training-length-label'
                          name='trainingLength'
                          label='Select Training Length'
                          data-testid='trainingLength'
                          value={additionalDetailsFormValues.trainingLength ?? ''}
                          onChange={handleAdditionalDetailsFieldUpdate as (e: SelectChangeEvent<typeof additionalDetailsFormValues.trainingLength>) => void}
                        >
                          {Object.values(TrainingLength).map(trainingLength => (
                            <MenuItem key={trainingLength} value={trainingLength} data-testid='trainingLengthOption'>
                              {trainingLength}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </>
                    )}
              </>
            )}
          </Box>
        }
    />)
}
